import type { SiteConfig } from "@lib/data/site-manager"

export type BrandConfig = {
  brandName: string
  shortName: string
  seoName: string
  logoUrl?: string
  logoDarkUrl?: string
  faviconUrl?: string
  contactEmail?: string
  contactPhone?: string
  whatsapp?: string
  address?: string
  defaultSeoTitle?: string
  defaultSeoDescription?: string
  socialLinks: Record<string, string>
}

const clean = (value: unknown) => {
  if (typeof value !== "string") {
    return undefined
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

const pick = (...values: unknown[]) => {
  for (const value of values) {
    const cleaned = clean(value)
    if (cleaned) {
      return cleaned
    }
  }

  return undefined
}

export function parseCsvList(value?: string): string[] {
  return (value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
}

export function getHostnameFromValue(value: string) {
  const trimmed = value.trim()
  if (!trimmed) {
    return undefined
  }

  try {
    return new URL(trimmed).hostname
  } catch {
    return trimmed.replace(/^https?:\/\//, "").split("/")[0] || undefined
  }
}

export function getConfiguredImageHosts() {
  return parseCsvList(process.env.NEXT_PUBLIC_IMAGE_HOSTS)
    .map(getHostnameFromValue)
    .filter((host): host is string => Boolean(host))
}

export function getConfiguredAssetPreconnectOrigins(branding?: BrandConfig) {
  const origins = parseCsvList(process.env.NEXT_PUBLIC_ASSET_PRECONNECT_ORIGINS)

  for (const url of [branding?.logoUrl, branding?.faviconUrl]) {
    if (!url) {
      continue
    }

    try {
      origins.push(new URL(url).origin)
    } catch {
      // Ignore invalid optional asset URLs.
    }
  }

  return [...new Set(origins)]
}

export function prefixedStorageKey(key: string) {
  const prefix = clean(process.env.NEXT_PUBLIC_STORAGE_PREFIX) || "storefront"
  return `${prefix}:${key}`
}

export function getBranding(config?: Pick<SiteConfig, "branding" | "footer"> | null): BrandConfig {
  const branding = config?.branding || ({} as SiteConfig["branding"])
  const footerContact = config?.footer?.contactInfo || {}

  const brandName =
    pick(branding.brandName, branding.storeName, process.env.NEXT_PUBLIC_BRAND_NAME) || "Store"
  const shortName =
    pick(branding.shortName, process.env.NEXT_PUBLIC_BRAND_SHORT_NAME, brandName) || "Store"
  const seoName =
    pick(
      branding.seoName,
      process.env.NEXT_PUBLIC_BRAND_SEO_NAME,
      branding.defaultSeoTitle
    ) || (brandName === "Store" ? "Online Store" : brandName)

  return {
    brandName,
    shortName,
    seoName,
    logoUrl: pick(branding.logoUrl, process.env.NEXT_PUBLIC_BRAND_LOGO_URL),
    logoDarkUrl: pick(branding.logoDarkUrl, process.env.NEXT_PUBLIC_BRAND_LOGO_DARK_URL),
    faviconUrl: pick(branding.faviconUrl, process.env.NEXT_PUBLIC_BRAND_FAVICON_URL),
    contactEmail: pick(
      branding.contactEmail,
      footerContact.email,
      process.env.NEXT_PUBLIC_BRAND_SUPPORT_EMAIL
    ),
    contactPhone: pick(
      branding.contactPhone,
      footerContact.phone,
      process.env.NEXT_PUBLIC_BRAND_SUPPORT_PHONE
    ),
    whatsapp: pick(branding.whatsapp, process.env.NEXT_PUBLIC_BRAND_WHATSAPP),
    address: pick(branding.address, process.env.NEXT_PUBLIC_BRAND_ADDRESS),
    defaultSeoTitle: pick(
      branding.defaultSeoTitle,
      process.env.NEXT_PUBLIC_BRAND_DEFAULT_SEO_TITLE
    ),
    defaultSeoDescription: pick(
      branding.defaultSeoDescription,
      process.env.NEXT_PUBLIC_BRAND_DEFAULT_SEO_DESCRIPTION
    ),
    socialLinks:
      branding.socialLinks && typeof branding.socialLinks === "object"
        ? branding.socialLinks
        : {},
  }
}
